/**
 * CUBIX isometric cube mark — brand identity v1.0
 *
 * Colours from cubix_brand_identity_v1.svg:
 *   Top face  : #9FE1CB (light teal, opacity 0.9)
 *   Right face: #1D9E75 (primary)
 *   Left face : #085041 (dark / onyx teal)
 *
 * Usage:
 *   <CubixMark />            — default 28px
 *   <CubixMark size={40} />  — larger lockup
 *   <CubixMark inverted />   — all-white variant for dark overlays
 */

interface CubixMarkProps {
  /** Square size in pixels. Default 28. */
  size?: number;
  /** When true, renders fully white (for use on tinted surfaces). */
  inverted?: boolean;
  className?: string;
}

export function CubixMark({ size = 28, inverted = false, className }: CubixMarkProps) {
  const top   = inverted ? 'rgba(255,255,255,0.35)' : '#9FE1CB';
  const right = inverted ? 'rgba(255,255,255,0.65)' : '#1D9E75';
  const left  = inverted ? 'rgba(255,255,255,0.45)' : '#085041';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 56 56"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      {/* Top face */}
      <polygon points="28,0 56,14 28,28 0,14" fill={top} opacity={inverted ? 1 : 0.9} />
      {/* Right face */}
      <polygon points="56,14 56,42 28,56 28,28" fill={right} />
      {/* Left face */}
      <polygon points="0,14 28,28 28,56 0,42" fill={left} />
      {/* Internal edge lines for depth */}
      <line x1="28" y1="28" x2="28" y2="56" stroke="#1D9E75" strokeWidth="0.75" opacity="0.5" />
      <line x1="28" y1="28" x2="0"  y2="14" stroke="#1D9E75" strokeWidth="0.75" opacity="0.3" />
      <line x1="28" y1="28" x2="56" y2="14" stroke="#1D9E75" strokeWidth="0.75" opacity="0.3" />
    </svg>
  );
}

/**
 * Full horizontal lockup: cube mark + "CUBIX" wordmark.
 * Uses Georgia serif to match the brand spec.
 */
export function CubixLockup({
  markSize = 24,
  dark = false,
}: {
  markSize?: number;
  /** dark=true → for use on white/light backgrounds */
  dark?: boolean;
}) {
  const cuColor  = dark ? '#0F6E56' : '#5DCAA5';  // "CU" in brand teal (dark) or accent (light)
  const bixColor = dark ? '#2C2C2A' : 'rgba(255,255,255,0.9)';
  const fontSize = Math.round(markSize * 1.5);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, lineHeight: 1 }}>
      <CubixMark size={markSize} inverted={!dark} />
      <span
        style={{
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          lineHeight: 1,
        }}
      >
        <span style={{ color: cuColor }}>CU</span>
        <span style={{ color: bixColor }}>BIX</span>
      </span>
    </span>
  );
}
